import { useCallback, useEffect, useImperativeHandle, useRef, type Ref } from 'react';

/**
 * The white pad he signs on with his finger, shared by the advance popup and
 * the loan receipt so the drawing and the compression are written once.
 *
 * `toPng()` trims to the ink with a small margin, flattens onto white (it gets
 * printed onto paper), and steps the scale down until the PNG is under ~30KB.
 * The result is uploaded to R2 by key — never a data URL, never in the
 * database (rule 7).
 */

export interface InkPadHandle {
  clear: () => void;
  toPng: () => Promise<Blob>;
}

interface Props {
  ref?: Ref<InkPadHandle>;
  /** Told when the pad goes from empty to inked, and back on Clear. */
  onInk: (hasInk: boolean) => void;
  hasInk: boolean;
}

const PAD_HEIGHT = 180;
const MAX_BYTES = 30 * 1024;

export default function InkPad({ ref, onInk, hasInk }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const contextRef = useRef<CanvasRenderingContext2D | null>(null);
  const drawingRef = useRef(false);
  const lastRef = useRef<{ x: number; y: number } | null>(null);
  const inkBoundsRef = useRef<{ minX: number; minY: number; maxX: number; maxY: number } | null>(null);

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(320, rect.width) * dpr;
    canvas.height = PAD_HEIGHT * dpr;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.scale(dpr, dpr);
    context.lineWidth = 2.4;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#111';
    contextRef.current = context;
    inkBoundsRef.current = null;
    onInk(false);
  }, [onInk]);

  useEffect(() => { setupCanvas(); }, [setupCanvas]);

  function positionOf(event: React.PointerEvent<HTMLCanvasElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  function noteInk(point: { x: number; y: number }) {
    const bounds = inkBoundsRef.current;
    inkBoundsRef.current = bounds
      ? {
          minX: Math.min(bounds.minX, point.x), minY: Math.min(bounds.minY, point.y),
          maxX: Math.max(bounds.maxX, point.x), maxY: Math.max(bounds.maxY, point.y),
        }
      : { minX: point.x, minY: point.y, maxX: point.x, maxY: point.y };
  }

  useImperativeHandle(ref, () => ({
    clear() {
      const canvas = canvasRef.current;
      const context = contextRef.current;
      if (canvas && context) context.clearRect(0, 0, canvas.width, canvas.height);
      inkBoundsRef.current = null;
      onInk(false);
    },

    async toPng() {
      const canvas = canvasRef.current;
      const bounds = inkBoundsRef.current;
      if (!canvas || !bounds) throw new Error('There is no signature to save.');

      const dpr = window.devicePixelRatio || 1;
      const pad = 8;
      const sx = Math.max(0, (bounds.minX - pad) * dpr);
      const sy = Math.max(0, (bounds.minY - pad) * dpr);
      const sw = Math.min(canvas.width - sx, (bounds.maxX - bounds.minX + pad * 2) * dpr);
      const sh = Math.min(canvas.height - sy, (bounds.maxY - bounds.minY + pad * 2) * dpr);

      for (const scale of [1, 0.75, 0.5, 0.35]) {
        const out = document.createElement('canvas');
        out.width = Math.max(1, Math.round(sw * scale));
        out.height = Math.max(1, Math.round(sh * scale));
        const context = out.getContext('2d');
        if (!context) throw new Error('Could not prepare the signature.');
        // White, not transparent: it gets printed onto a paper receipt.
        context.fillStyle = '#fff';
        context.fillRect(0, 0, out.width, out.height);
        context.drawImage(canvas, sx, sy, sw, sh, 0, 0, out.width, out.height);

        const blob = await new Promise<Blob | null>((resolve) =>
          out.toBlob((b) => resolve(b), 'image/png'),
        );
        if (blob && blob.size <= MAX_BYTES) return blob;
        if (scale === 0.35 && blob) return blob;
      }
      throw new Error('Could not prepare the signature.');
    },
  }), [onInk]);

  return (
    <div className="sigwrap">
      <canvas
        ref={canvasRef}
        className="sigpad"
        onPointerDown={(event) => {
          event.preventDefault();
          drawingRef.current = true;
          const point = positionOf(event);
          lastRef.current = point;
          noteInk(point);
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (!drawingRef.current) return;
          event.preventDefault();
          const context = contextRef.current;
          const last = lastRef.current;
          if (!context || !last) return;
          const point = positionOf(event);
          context.beginPath();
          context.moveTo(last.x, last.y);
          context.lineTo(point.x, point.y);
          context.stroke();
          lastRef.current = point;
          noteInk(point);
          if (!hasInk) onInk(true);
        }}
        onPointerUp={() => { drawingRef.current = false; }}
        onPointerCancel={() => { drawingRef.current = false; }}
        onPointerLeave={() => { drawingRef.current = false; }}
      />
      <div className="sigline" />
      {!hasInk && <div className="sighint">sign above with your finger</div>}
    </div>
  );
}
